import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store/database';
import { SUPPORTED_LANGUAGES, type SupportedLang } from '../../config/house.config';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import PasswordInput from '../../components/ui/PasswordInput';

export default function RegisterPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const register = useStore(s => s.register);
  const [form, setForm] = useState({ email: '', password: '', firstName: '', country: '', inviteCode: '' });
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.email || !form.password || !form.firstName || !form.country) { setError(t('auth.fieldRequired')); return; }
    if (!/\S+@\S+\.\S+/.test(form.email)) { setError(t('auth.invalidEmail')); return; }
    const lang = (SUPPORTED_LANGUAGES.includes(i18n.language as SupportedLang) ? i18n.language : 'en') as SupportedLang;
    const res = register(form.email, form.password, form.firstName, form.country, lang, form.inviteCode || undefined);
    if (res.ok) navigate('/gateway');
    else setError(t(res.error || 'common.error'));
  };

  const inp = "w-full border-b border-charcoal/15 py-2.5 text-sm font-sans bg-transparent focus:border-charcoal/40 transition-colors placeholder:text-charcoal/25";

  return (
    <div className="min-h-dvh bg-ivory flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center space-y-2">
          <h1 className="font-serif text-3xl tracking-widest-xl">AH</h1>
          <p className="text-[11px] tracking-widest-2xl uppercase text-charcoal/40 font-sans">{t('auth.registerTitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input type="text" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} placeholder={t('auth.firstName')} className={inp} />
          <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder={t('auth.email')} className={inp} />
          <PasswordInput value={form.password} onChange={v => setForm(f => ({ ...f, password: v }))} placeholder={t('auth.password')} />
          <input type="text" value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} placeholder={t('auth.country')} className={inp} />
          <input type="text" value={form.inviteCode} onChange={e => setForm(f => ({ ...f, inviteCode: e.target.value }))} placeholder={t('auth.inviteCodeOptional')} className={inp} />
          {error && <p className="text-xs text-burgundy font-sans">{error}</p>}
          <button type="submit" className="w-full py-3 bg-charcoal text-ivory text-[11px] tracking-widest-xl uppercase font-sans hover:bg-charcoal/90 transition-colors">
            {t('auth.submit')}
          </button>
        </form>

        <div className="text-center">
          <Link to="/login" className="text-[11px] tracking-widest uppercase font-sans text-charcoal/40 hover:text-charcoal transition-colors">
            {t('auth.hasAccount')} {t('auth.enterHouse')}
          </Link>
        </div>
        <div className="flex justify-center pt-4"><LanguageSwitcher /></div>
      </div>
    </div>
  );
}
